import React, { useContext, useEffect, useRef, useState } from 'react';
import AdyenCheckout from '@adyen/adyen-web';
import { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import { DrawerPopup } from '../../dialog-layout/drawer-popup/DrawerPopup';
import { Button } from '../../elements/button/Button';
import { getSafeTranslation } from '../../../helpers/localization.helper';
import { TranslationsContext } from '../../../contexts/translations-context';
import { IAdyenPaymentData, IPaymentModel, IPaymentSubmissionState } from '../../../interfaces/adyen-payment.interface';
import { AdyenHandleAction } from '../../../reducers/payment.reducer.types';
import DropinElement from '@adyen/adyen-web/dist/types/components/Dropin';
import Config from '../../../config';
import SwishElement from '@adyen/adyen-web/dist/types/components/Swish';

interface SwishPayType {
	adyenClientKey: string;
	adyenPaymentMethodsResponse: IPaymentModel;
	handleAction: AdyenHandleAction;
	onSubmit: (state: IPaymentSubmissionState, dropin: SwishElement) => void;
	onAdditionalDetails: (state: IAdyenPaymentData, dropin: SwishElement) => void;
	onCancelClick: () => void;
	onOverlayClick: () => void;
}

export const SwishPay: React.FC<SwishPayType> = (
	{
		adyenClientKey,
		adyenPaymentMethodsResponse,
		handleAction,
		onSubmit,
		onAdditionalDetails,
		onCancelClick,
		onOverlayClick
	}
) => {
	const translations = useContext(TranslationsContext);
	const swishPayRef = useRef<HTMLDivElement>(null);
	const [dropin, setDropin] = useState<DropinElement>(null);

	const checkout = new AdyenCheckout(getAdyenConfiguration(
			adyenPaymentMethodsResponse,
			adyenClientKey,
			Config.adyenLocale,
			Config.adyenEnvironment,
			onSubmit,
			onAdditionalDetails,
		)
	);

	useEffect(() => {
		if (!adyenClientKey || !adyenPaymentMethodsResponse) {
			return;
		}
		setDropin(checkout.create('dropin').mount(swishPayRef.current));
	}, [adyenPaymentMethodsResponse, adyenClientKey]);

	useEffect(() => {
		if (!dropin) {
			return;
		}
		dropin.submit();
	}, [dropin]);

	useEffect(() => {
		if (!handleAction || !dropin) {
			return;
		}
		dropin.handleAction(handleAction);
	}, [handleAction]);

	return (
		<DrawerPopup
			title={
				getSafeTranslation('SwishPayPopup.title', translations)
			}
			leftControl={
				<Button isText onClick={onCancelClick}>{
					getSafeTranslation('SwishPayPopup.cancelButtonCaption', translations)
				}</Button>
			}
			onOverlayClick={onOverlayClick}
		>
			<div className="swish-pay">
				<div ref={swishPayRef}/>
			</div>
		</DrawerPopup>
	);
}

const getAdyenConfiguration = (paymentMethodsResponse, clientAdyenKey, adyenLocale, adyenEnvironment, onSubmit, onAdditionalDetails): CoreOptions => ({
	paymentMethodsResponse,
	clientKey: clientAdyenKey,
	locale: adyenLocale,
	environment: adyenEnvironment,
	onSubmit: onSubmit,
	onAdditionalDetails: onAdditionalDetails,
});
